(() => {
  if (customElements.get('pushgo-package-selector')) return;

  class PushGoPackageSelector extends HTMLElement {
    connectedCallback() {
      if (this.shadowRoot) return;
      const assetsNode = this.querySelector('[data-pushgo-assets]');
      const dataNode = this.querySelector('[data-pushgo-data]');
      const metaobjectsNode = this.querySelector('[data-pushgo-metaobjects]');
      const ASSETS = JSON.parse(assetsNode?.textContent || '{}');
      const DATA = JSON.parse(dataNode?.textContent || '{"categories":[]}');
      const METAOBJECT_PRODUCTS = JSON.parse(metaobjectsNode?.textContent || '[]');
      const PRODUCT_LINKS = {};
      if (METAOBJECT_PRODUCTS.length) {
        DATA.categories.forEach(category => {
          category.packages.forEach(pkg => {
            pkg.products = METAOBJECT_PRODUCTS
              .filter(product => product.packages?.[pkg.name] === true)
              .sort((a, b) => (a.displayOrder - b.displayOrder) || a.name.localeCompare(b.name))
              .map(product => ({ ...product, id: `${pkg.name}-${product.handle}` }));
          });
        });
      }
      const host = this;
      const ownerDocument = this.ownerDocument;
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `<link rel="stylesheet" href="${this.dataset.stylesheet}"><main class="pushgo-app" data-app></main><div class="sr-only" data-announcer aria-live="polite"></div>`;

    

    const app = root.querySelector('[data-app]');
    const announcer = root.querySelector('[data-announcer]');
    const state = { category: null, package: null, section: 'EAT', lastFocus: null };
    let activeDetailClose = null;

    const categoryStyles = {
      'The Meeting Room in a Box': { accent: '#13e7ff', rgb: '19, 231, 255', icon: 'meeting-room-64px.svg' },
      'Eat. Pray. Lift.': { accent: '#ffc572', rgb: '255, 197, 114', icon: null }
    };

    const sectionStyles = {
      EAT: { accent: '#ffc572', rgb: '255, 197, 114', icon: 'eat-20px.svg' },
      PRAY: { accent: '#fffa97', rgb: '255, 250, 151', icon: 'pray-20px.svg' },
      LIFT: { accent: '#abfe8b', rgb: '171, 254, 139', icon: 'lift-20px.svg' }
    };

    function escapeHtml(value = '') {
      return String(value).replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
    }

    function announce(message) {
      announcer.textContent = '';
      requestAnimationFrame(() => { announcer.textContent = message; });
    }

    function brand(subtitle = 'Select an application') {
      return `<header class="brand">
        <h1 class="brand__title"><span>Learn about</span><img class="brand__logo" src="${ASSETS['asset-PushGo-logo.png']}" alt="PushGo"></h1>
        <p class="brand__subtitle">${escapeHtml(subtitle)}</p>
      </header>`;
    }

    function backIcon() { return `<img src="${ASSETS['arrow_back_20px.svg']}" alt="">`; }
    function packageIcon() { return `<img src="${ASSETS['package_20px.svg']}" alt="">`; }

    function setTheme(category, section = null) {
      const style = section ? sectionStyles[section] : categoryStyles[category] || categoryStyles['The Meeting Room in a Box'];
      host.style.setProperty('--accent', style.accent);
      host.style.setProperty('--accent-rgb', style.rgb);
    }

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function transitionScreen(render, duration = 560) {
      if (activeDetailClose) activeDetailClose({ immediate: true, restoreFocus: false });
      const current = app.querySelector('.screen');
      if (!current || prefersReducedMotion()) return render();
      current.classList.add('screen--leaving');
      window.setTimeout(render, duration);
    }

    function renderMain() {
      state.category = null;
      state.package = null;
      setTheme('The Meeting Room in a Box');
      app.innerHTML = `<section class="screen main-screen" aria-labelledby="main-title">
        ${brand().replace('<h1 ', '<h1 id="main-title" ')}
        <div class="choice-grid">
          <button class="category-card" type="button" data-category="The Meeting Room in a Box" style="--card-color:#13e7ff;--card-rgb:19,231,255">
            <span class="category-card__content">
              <img class="category-card__icon" src="${ASSETS['meeting-room-64px.svg']}" alt="">
              <span class="category-card__title">The Meeting Room in a Box</span>
            </span>
          </button>
          <button class="category-card" type="button" data-category="Eat. Pray. Lift." style="--card-color:#ffc572;--card-rgb:255,197,114">
            <span class="category-card__content">
              <span class="category-card__icons" aria-hidden="true">
                <span><img class="category-card__icon" src="${ASSETS['eat-64px.svg']}" alt="">Eat</span>
                <span><img class="category-card__icon" src="${ASSETS['pray-64px.svg']}" alt="">Pray</span>
                <span><img class="category-card__icon" src="${ASSETS['lift-64px.svg']}" alt="">Lift</span>
              </span>
              <span class="sr-only">Eat. Pray. Lift.</span>
            </span>
          </button>
        </div>
      </section>`;

      app.querySelectorAll('[data-category]').forEach(button => {
        button.addEventListener('click', () => transitionScreen(() => renderPackages(button.dataset.category)));
      });
      announce('Main category selection');
    }

    function renderPackages(categoryName) {
      state.category = categoryName;
      state.package = null;
      setTheme(categoryName);
      const packages = DATA.categories.find(item => item.name === categoryName).packages;
      const columns = Math.min(packages.length, 5);
      app.innerHTML = `<section class="screen package-screen ${categoryName === 'Eat. Pray. Lift.' ? 'package-screen--epl' : ''}" aria-labelledby="package-title">
        ${brand('Select a package').replace('<h1 ', '<h1 id="package-title" ')}
        <div class="package-grid" style="--columns:${columns}">
          ${packages.map(pkg => `<button class="package-card" type="button" data-package="${escapeHtml(pkg.name)}" style="--card-image:url('${ASSETS[pkg.cardAsset]}')" aria-label="Select ${escapeHtml(pkg.name)}: ${escapeHtml(pkg.description)}">
            <span class="package-card__copy">
              <span class="package-card__name">${escapeHtml(pkg.name)}</span>
              <span class="package-card__desc">${escapeHtml(pkg.description)}</span>
            </span>
          </button>`).join('')}
        </div>
        <div class="package-scroll-indicator" aria-hidden="true"><span class="package-scroll-indicator__thumb"></span></div>
        <div class="nav-row nav-row--center">
          <button class="asset-button nav-control package-back-control" type="button" data-main aria-label="Back to main category selection"><span class="nav-control__icon" style="--button-icon:url('${ASSETS['arrow_back_20px.svg']}')" aria-hidden="true"></span><span>Back to main</span></button>
        </div>
      </section>`;

      app.querySelector('[data-main]').addEventListener('click', () => transitionScreen(renderMain));
      const packageGrid = app.querySelector('.package-grid');
      const scrollIndicator = app.querySelector('.package-scroll-indicator');
      const scrollThumb = app.querySelector('.package-scroll-indicator__thumb');
      if (packageGrid && scrollIndicator && scrollThumb) {
        const syncScrollIndicator = () => {
          const trackWidth = scrollIndicator.clientWidth;
          const maxScroll = Math.max(0, packageGrid.scrollWidth - packageGrid.clientWidth);
          if (!trackWidth) return;
          const visibleRatio = Math.min(1, packageGrid.clientWidth / packageGrid.scrollWidth);
          const thumbWidth = Math.max(48, trackWidth * visibleRatio);
          const maxThumbTravel = Math.max(0, trackWidth - thumbWidth);
          const progress = maxScroll ? packageGrid.scrollLeft / maxScroll : 0;
          scrollThumb.style.width = `${thumbWidth}px`;
          scrollThumb.style.transform = `translateX(${maxThumbTravel * progress}px)`;
        };
        packageGrid.addEventListener('scroll', syncScrollIndicator, { passive: true });
        requestAnimationFrame(syncScrollIndicator);

        let gridDrag = null;
        let suppressPackageClick = false;
        packageGrid.addEventListener('pointerdown', event => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          gridDrag = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startScroll: packageGrid.scrollLeft,
            moved: false
          };
        });
        packageGrid.addEventListener('pointermove', event => {
          if (!gridDrag || event.pointerId !== gridDrag.pointerId) return;
          const distance = event.clientX - gridDrag.startX;
          if (Math.abs(distance) > 4) {
            if (!gridDrag.moved) packageGrid.setPointerCapture?.(event.pointerId);
            gridDrag.moved = true;
            packageGrid.classList.add('is-dragging');
            packageGrid.scrollLeft = gridDrag.startScroll - distance;
            event.preventDefault();
          }
        });
        const finishGridDrag = event => {
          if (!gridDrag || event.pointerId !== gridDrag.pointerId) return;
          suppressPackageClick = gridDrag.moved;
          packageGrid.classList.remove('is-dragging');
          if (packageGrid.hasPointerCapture?.(event.pointerId)) {
            packageGrid.releasePointerCapture(event.pointerId);
          }
          gridDrag = null;
        };
        packageGrid.addEventListener('pointerup', finishGridDrag);
        packageGrid.addEventListener('pointercancel', finishGridDrag);
        packageGrid.addEventListener('click', event => {
          if (!suppressPackageClick) return;
          event.preventDefault();
          event.stopPropagation();
          suppressPackageClick = false;
        }, true);

        let indicatorPointerId = null;
        const scrollFromIndicator = clientX => {
          const trackRect = scrollIndicator.getBoundingClientRect();
          const thumbWidth = scrollThumb.getBoundingClientRect().width;
          const availableTrack = Math.max(1, trackRect.width - thumbWidth);
          const position = Math.min(availableTrack, Math.max(0, clientX - trackRect.left - (thumbWidth / 2)));
          const maxScroll = Math.max(0, packageGrid.scrollWidth - packageGrid.clientWidth);
          packageGrid.scrollLeft = (position / availableTrack) * maxScroll;
        };
        scrollIndicator.addEventListener('pointerdown', event => {
          if (event.button !== 0) return;
          event.preventDefault();
          indicatorPointerId = event.pointerId;
          scrollIndicator.setPointerCapture?.(event.pointerId);
          scrollFromIndicator(event.clientX);
        });
        scrollIndicator.addEventListener('pointermove', event => {
          if (event.pointerId !== indicatorPointerId) return;
          event.preventDefault();
          scrollFromIndicator(event.clientX);
        });
        const finishIndicatorDrag = event => {
          if (event.pointerId !== indicatorPointerId) return;
          scrollIndicator.releasePointerCapture?.(event.pointerId);
          indicatorPointerId = null;
        };
        scrollIndicator.addEventListener('pointerup', finishIndicatorDrag);
        scrollIndicator.addEventListener('pointercancel', finishIndicatorDrag);
      }
      app.querySelectorAll('[data-package]').forEach(button => {
        button.addEventListener('click', () => {
          const pkg = packages.find(item => item.name === button.dataset.package);
          selectPackage(button, pkg);
        });
      });
      announce(`${categoryName} package selection`);
    }

    function selectPackage(button, pkg) {
      const screen = button.closest('.package-screen');
      if (!screen || screen.classList.contains('is-selecting')) return;
      screen.classList.add('is-selecting');
      button.classList.add('is-selected');
      if (!prefersReducedMotion() && button.animate) {
        const rect = button.getBoundingClientRect();
        const dx = (window.innerWidth / 2) - (rect.left + rect.width / 2);
        button.animate(
          [{ transform: 'translateX(0) scale(1)' }, { transform: `translateX(${dx}px) scale(1.03)` }],
          { duration: 840, easing: 'cubic-bezier(.22,.75,.25,1)', fill: 'forwards' }
        );
      }
      window.setTimeout(() => playReveal(pkg), prefersReducedMotion() ? 20 : 860);
    }

    function sceneAssetFor(pkg, section = state.section) {
      return state.category === 'Eat. Pray. Lift.' ? pkg.sectionAssets[section || 'EAT'] : pkg.sceneAsset;
    }

    function playReveal(pkg) {
      state.package = pkg;
      state.section = 'EAT';
      const accent = categoryStyles[state.category];
      setTheme(state.category);
      const skipAsset = state.category === 'Eat. Pray. Lift.' ? 'UI-Example_Skip video white.svg' : 'UI-Example_Skip video blue.svg';
      const sceneAsset = sceneAssetFor(pkg, 'EAT');
      app.innerHTML = `<section class="screen video-screen ${state.category === 'Eat. Pray. Lift.' ? 'video-screen--epl' : ''}" aria-label="${escapeHtml(pkg.name)} package reveal">
        <video class="reveal-video" playsinline muted preload="auto" aria-label="${escapeHtml(pkg.name)} package reveal video">
          <source src="${ASSETS[pkg.videoAsset]}" type="video/mp4">
        </video>
        <img class="reveal-freeze-image" src="${ASSETS[sceneAsset]}" alt="">
        <button class="asset-button video-action" type="button" data-skip aria-label="Skip reveal video"><img src="${ASSETS[skipAsset]}" alt=""></button>
      </section>`;
      const screen = app.querySelector('.video-screen');
      const video = app.querySelector('video');
      const freeze = app.querySelector('.reveal-freeze-image');
      const skip = app.querySelector('[data-skip]');
      let completed = false;
      const finish = () => {
        if (completed) return;
        completed = true;
        screen.classList.add('is-finishing');
        const showFreezeFrame = () => {
          requestAnimationFrame(() => {
            freeze.classList.add('is-visible');
            window.setTimeout(() => renderProducts({ fromReveal: true }), prefersReducedMotion() ? 20 : 220);
          });
        };
        if (freeze.decode) freeze.decode().catch(() => {}).then(showFreezeFrame);
        else if (freeze.complete) showFreezeFrame();
        else freeze.addEventListener('load', showFreezeFrame, { once: true });
      };
      skip.addEventListener('click', finish);
      video.addEventListener('ended', finish, { once: true });
      video.addEventListener('error', () => showVideoFallback(pkg), { once: true });
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => showVideoFallback(pkg));
      announce(`Playing ${pkg.name} package reveal`);
    }

    function showVideoFallback(pkg) {
      if (!state.package || state.package.name !== pkg.name) return;
      app.innerHTML = `<section class="screen video-screen">
        <div class="video-fallback" role="alert">
          <h2>${escapeHtml(pkg.name)} reveal unavailable</h2>
          <p>The preview video could not be played. You can continue directly to the package products.</p>
          <button class="action action--primary" type="button" data-continue>Continue to products</button>
        </div>
      </section>`;
      app.querySelector('[data-continue]').addEventListener('click', () => renderProducts({ fromReveal: true }));
      announce('Video unavailable. Continue to products.');
    }

    function productCard(product, calloutIndex = 0) {
      const hasDetail = Boolean(
        product.linkToHandle ||
        product.detailTargetSku ||
        product.description
      );
      const productImage = product.imageUrl || ASSETS[product.imageAsset] || '';
      const tag = hasDetail ? 'button' : 'div';
      const attrs = hasDetail
        ? `type="button" data-product-id="${escapeHtml(product.id)}" aria-label="Learn more about ${escapeHtml(product.name)}"`
        : `role="group" aria-label="${escapeHtml(product.name)}"`;
      return `<${tag} class="product-card" style="--callout-index:${calloutIndex}" ${attrs}>
        <img class="product-card__image" src="${productImage}" alt="${escapeHtml(product.name)}">
        <span class="product-card__copy">
          <span class="product-card__name">${escapeHtml(product.name)}</span>
          ${product.sku ? `<span class="product-card__sku">${escapeHtml(product.sku)}</span>` : ''}
          ${hasDetail ? `<span class="product-card__learn">Learn more</span>` : ''}
        </span>
      </${tag}>`;
    }

    function renderProducts(options = {}) {
      const pkg = state.package;
      if (!pkg) return renderMain();
      const isEpl = state.category === 'Eat. Pray. Lift.';
      if (!isEpl) state.section = null;
      const style = isEpl ? sectionStyles[state.section] : categoryStyles[state.category];
      setTheme(state.category, isEpl ? state.section : null);
      const lead = pkg.products[0];
      const remaining = pkg.products.slice(1);
      const left = remaining.slice(0, Math.min(3, remaining.length));
      const right = remaining.slice(left.length);
      const sceneAsset = sceneAssetFor(pkg);
      const tabsAsset = `UI-Example_${state.section} SELECTED.svg`;
      app.innerHTML = `<section class="screen product-screen ${isEpl ? 'product-screen--epl' : ''} ${options.fromReveal ? 'is-reveal-enter is-video-handoff' : ''}" style="--accent:${style.accent};--accent-rgb:${style.rgb}" aria-labelledby="product-heading">
        <button class="asset-button mobile-product-back" type="button" data-packages-mobile aria-label="Back to package selection"><span class="mobile-product-back__icon" style="--button-icon:url('${ASSETS['arrow_back_20px.svg']}')" aria-hidden="true"></span></button>
        <div class="nav-row">
          <button class="asset-button nav-control nav-control--home" type="button" data-main aria-label="Back to main category selection" title="Back to main"><span class="nav-control__icon" style="--button-icon:url('${ASSETS['home_20px.svg']}')" aria-hidden="true"></span></button>
          <button class="asset-button nav-control nav-control--packages" type="button" data-packages aria-label="View all packages"><img class="nav-control__icon-image" src="${ASSETS['package_20px.svg']}" alt=""><span>View all packages</span></button>
        </div>
        <div class="product-shell">
          ${isEpl ? `<div class="section-tabs" role="tablist" aria-label="Choose setting">
            <img class="section-tabs__image" src="${ASSETS[tabsAsset]}" alt="">
            ${Object.keys(sectionStyles).map(section => `<button class="section-tab section-tab--${section.toLowerCase()}" type="button" role="tab" aria-label="${section}" aria-selected="${state.section === section}" data-section="${section}"></button>`).join('')}
          </div>` : ''}
          <div class="product-stage">
            <div class="scene-wrap"><img class="scene-image" src="${ASSETS[sceneAsset]}" alt="${escapeHtml(pkg.name)} ${isEpl ? state.section.toLowerCase() + ' setting' : 'meeting room'}"></div>
            <div class="product-list-scroll" aria-label="Products included in this package">
              <div class="lead-card">${lead ? productCard(lead, 0) : '<div class="product-card" role="status"><span class="product-card__copy"><span class="product-card__name">No products configured for this package</span></span></div>'}</div>
              <div class="product-column product-column--left">${left.map((product, index) => productCard(product, index + 1)).join('')}</div>
              <div class="product-column product-column--right">${right.map((product, index) => productCard(product, index + left.length + 1)).join('')}</div>
            </div>
          </div>
          <div class="package-caption">
            <p class="package-caption__eyebrow">PushGo ${escapeHtml(pkg.name)}</p>
            <h2 class="package-caption__title" id="product-heading">${escapeHtml(pkg.description)}</h2>
          </div>
        </div>
      </section>`;

      app.querySelector('[data-main]').addEventListener('click', () => transitionScreen(renderMain));
      app.querySelectorAll('[data-packages], [data-packages-mobile]').forEach(button => {
        button.addEventListener('click', () => transitionScreen(() => renderPackages(state.category)));
      });
      app.querySelectorAll('[data-section]').forEach(tab => {
        tab.addEventListener('click', () => {
          switchSection(tab.dataset.section);
        });
      });
      app.querySelectorAll('[data-product-id]').forEach(button => {
        button.addEventListener('click', () => {
          state.lastFocus = button;
          const product = pkg.products.find(item => item.id === button.dataset.productId);
          openDetail(product);
        });
      });
      announce(`${pkg.name}${isEpl ? ` ${state.section}` : ''} product list`);
    }

    function switchSection(nextSection) {
      if (state.category !== 'Eat. Pray. Lift.' || nextSection === state.section) return;
      const image = app.querySelector('.scene-image');
      const tabsImage = app.querySelector('.section-tabs__image');
      const productScreen = app.querySelector('.product-screen');
      if (!image || image.classList.contains('is-switching')) return;
      image.classList.add('is-switching');
      productScreen?.classList.add('is-section-switching');
      const complete = () => {
        state.section = nextSection;
        setTheme(state.category, nextSection);
        productScreen?.style.setProperty('--accent', sectionStyles[nextSection].accent);
        productScreen?.style.setProperty('--accent-rgb', sectionStyles[nextSection].rgb);
        image.src = ASSETS[sceneAssetFor(state.package, nextSection)];
        image.alt = `${state.package.name} ${nextSection.toLowerCase()} setting`;
        tabsImage.src = ASSETS[`UI-Example_${nextSection} SELECTED.svg`];
        app.querySelectorAll('[data-section]').forEach(tab => tab.setAttribute('aria-selected', String(tab.dataset.section === nextSection)));
        const eyebrow = app.querySelector('.package-caption__eyebrow');
        if (eyebrow) eyebrow.textContent = `PushGo ${state.package.name}`;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          image.classList.remove('is-switching');
          productScreen?.classList.remove('is-section-switching');
        }));
        announce(`${state.package.name} ${nextSection} product list`);
      };
      window.setTimeout(complete, prefersReducedMotion() ? 20 : 640);
    }

    function openDetail(product) {
      if (!product) return;
      const pkg = state.package;
      const isEpl = state.category === 'Eat. Pray. Lift.';
      const linkedByMetaobject = product.linkToHandle && typeof METAOBJECT_PRODUCTS !== 'undefined'
        ? METAOBJECT_PRODUCTS.find(item => item.handle === product.linkToHandle)
        : null;
      const linkedBySku = product.detailTargetSku
        ? pkg.products.find(item => item.sku === product.detailTargetSku)
        : null;
      const shown = linkedByMetaobject || linkedBySku || product;
      if (!shown.description) return;
      if (activeDetailClose) activeDetailClose({ immediate: true, restoreFocus: false });
      const productUrl = shown.productUrl || PRODUCT_LINKS[shown.sku] || '';
      const productImage = shown.imageUrl || ASSETS[shown.imageAsset] || '';
      const sceneAsset = sceneAssetFor(pkg);
      const detailView = ownerDocument.createElement('div');
      detailView.className = `detail-view${isEpl ? ' detail-view--epl' : ''}`;
      detailView.tabIndex = -1;
      detailView.setAttribute('role', 'dialog');
      detailView.setAttribute('aria-modal', 'true');
      detailView.setAttribute('aria-labelledby', 'detail-title');
      detailView.innerHTML = `<button class="asset-button detail-return detail-close-button" type="button" data-close aria-label="Close product details"><span class="detail-close-button__icon" style="--button-icon:url('${ASSETS['close_20px.svg']}')" aria-hidden="true"></span></button>
        <img class="detail-scene" src="${ASSETS[sceneAsset]}" alt="${escapeHtml(pkg.name)} ${isEpl ? state.section.toLowerCase() + ' setting' : 'meeting room'}">
        <aside class="detail-panel">
          <img class="detail-image" src="${productImage}" alt="${escapeHtml(shown.name)}">
          <h2 class="detail-title" id="detail-title">${escapeHtml(shown.name)}</h2>
          ${shown.sku ? `<p class="detail-sku">${escapeHtml(shown.sku)}</p>` : ''}
          <p class="detail-description">${escapeHtml(shown.description)}</p>
          <a class="detail-product-link" ${productUrl ? `href="${escapeHtml(productUrl)}" target="_blank" rel="noopener noreferrer"` : 'aria-disabled="true"'}>View Product Details ↗</a>
        </aside>`;
      app.append(detailView);
      const productScreen = app.querySelector('.product-screen');
      productScreen?.classList.add('is-detail-opening');
      requestAnimationFrame(() => detailView.classList.add('is-open'));
      const closeButton = detailView.querySelector('[data-close]');
      detailView.focus({ preventScroll: true });
      const close = ({ immediate = false, restoreFocus = true } = {}) => {
        if (!detailView.isConnected) return;
        detailView.classList.remove('is-open');
        productScreen?.classList.remove('is-detail-opening');
        const finishClose = () => {
          detailView.remove();
          if (restoreFocus && state.lastFocus?.isConnected) state.lastFocus.focus();
          if (activeDetailClose === close) activeDetailClose = null;
        };
        if (immediate) finishClose();
        else setTimeout(finishClose, prefersReducedMotion() ? 20 : 1000);
        ownerDocument.removeEventListener('keydown', onKeydown);
      };
      const onKeydown = event => {
        if (event.key === 'Escape') close();
        if (event.key === 'Tab') {
          const focusable = [...detailView.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])')];
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && ownerDocument.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && ownerDocument.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      };
      activeDetailClose = close;
      closeButton.addEventListener('click', close);
      detailView.addEventListener('click', event => {
        if (event.target === detailView) close();
      });
      ownerDocument.addEventListener('keydown', onKeydown);
      announce(`${shown.name} product detail`);
    }

    renderMain();
    }
  }

  customElements.define('pushgo-package-selector', PushGoPackageSelector);
})();
